import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchAllPages } from './fetch-all-pages';
import type {
  PositionTraining,
  PositionListItem,
  RecurrenceConfig,
  CreateRecurrenceInput,
} from '@/types';
import { KEYS } from './course-builder.keys';

// ── Positions ──

/** Todos los puestos (todas las páginas) para el selector del constructor de cursos. */
export function usePositions() {
  return useQuery({
    queryKey: ['positions', 'all'],
    queryFn: () => fetchAllPages<PositionListItem>('/positions'),
  });
}

export function useCoursePositions(courseId: number) {
  return useQuery({
    queryKey: KEYS.positions(courseId),
    queryFn: () => api.get<PositionTraining[]>(`/courses/${courseId}/positions`),
    select: (res) => res.data,
    enabled: courseId > 0,
  });
}

export function useLinkPositionTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseId,
      data,
    }: {
      courseId: number;
      data: { position_id: number; is_required?: boolean; priority?: number };
    }) => api.post(`/courses/${courseId}/positions`, data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.positions(v.courseId) }),
  });
}

export function useUpdatePositionTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      courseId,
      positionId,
      data,
    }: {
      courseId: number;
      positionId: number;
      data: { is_required?: boolean; priority?: number };
    }) => api.patch(`/courses/${courseId}/positions/${positionId}`, data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.positions(v.courseId) }),
  });
}

export function useUnlinkPositionTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, positionId }: { courseId: number; positionId: number }) =>
      api.delete(`/courses/${courseId}/positions/${positionId}`),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.positions(v.courseId) }),
  });
}

// ── Recurrence ──

export function useCourseRecurrence(courseId: number) {
  return useQuery({
    queryKey: KEYS.recurrence(courseId),
    queryFn: () => api.get<RecurrenceConfig>(`/courses/${courseId}/recurrence`),
    select: (res) => res.data,
    enabled: courseId > 0,
    retry: false,
  });
}

export function useSetRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }: { courseId: number; data: CreateRecurrenceInput }) =>
      api.post(`/courses/${courseId}/recurrence`, data),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: KEYS.recurrence(v.courseId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) });
    },
  });
}

export function useDeleteRecurrence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId: number) => api.delete(`/courses/${courseId}/recurrence`),
    onSuccess: (_, courseId) => {
      qc.invalidateQueries({ queryKey: KEYS.recurrence(courseId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(courseId) });
    },
  });
}
